/**
 * AnalyticsDashboard — F2: Usage Analytics
 * SVG bar charts (no external libs), queries per day, active users, top domains, cache hit rate.
 * Pattern follows CostDashboard.tsx.
 */
import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../api';
import { CORPUS_STATS } from '../constants';

const API_BASE = import.meta.env.VITE_API_URL || '';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DailyPoint {
  date: string;
  queries: number;
  avg_latency_ms: number;
  active_users: number;
}

interface TopDomain {
  domain: string;
  count: number;
}

interface AnalyticsData {
  daily: DailyPoint[];
  today: { queries: number; avg_latency_ms: number; active_users: number };
  week: { queries: number; active_users: number };
  top_domains: TopDomain[];
  cache: { entries: number; total_hits: number; hit_rate_pct: number };
}

// ─── SVG Bar Chart ────────────────────────────────────────────────────────────

function BarChart({
  data,
  valueKey,
  label,
  color = 'var(--accent)',
  height = 80,
}: {
  data: DailyPoint[];
  valueKey: 'queries' | 'active_users' | 'avg_latency_ms';
  label: string;
  color?: string;
  height?: number;
}) {
  if (!data.length) return null;
  const values = data.map(d => d[valueKey] as number);
  const maxVal = Math.max(...values, 1);
  const barW = 100 / data.length;

  return (
    <div className="cd-chart-wrap" aria-label={label}>
      <svg
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        className="cd-chart-svg"
        role="img"
        aria-label={label}
      >
        {data.map((d, i) => {
          const val = d[valueKey] as number;
          const barH = maxVal > 0 ? (val / maxVal) * (height - 10) : 0;
          const x = i * barW;
          const y = height - barH;
          const isToday = i === data.length - 1;
          const tooltip = `${d.date}: ${val}`;
          return (
            <g key={d.date}>
              <rect
                x={x + barW * 0.1}
                y={y}
                width={barW * 0.8}
                height={barH}
                fill={isToday ? color : 'var(--accent-soft, rgba(99,102,241,0.3))'}
                rx="1"
              />
              <title>{tooltip}</title>
            </g>
          );
        })}
        <line x1="0" y1={height - 0.5} x2="100" y2={height - 0.5} stroke="var(--border, #334)" strokeWidth="0.5" />
      </svg>
      <div className="cd-chart-labels">
        <span>{data[0]?.date?.slice(5) ?? ''}</span>
        <span>{data[Math.floor(data.length / 2)]?.date?.slice(5) ?? ''}</span>
        <span>{data[data.length - 1]?.date?.slice(5) ?? ''}</span>
      </div>
    </div>
  );
}

// ─── Horizontal Domain Bar Chart ──────────────────────────────────────────────

function DomainChart({ domains }: { domains: TopDomain[] }) {
  if (!domains.length) return <div className="cd-empty">No domain data yet</div>;
  const maxCount = Math.max(...domains.map(d => d.count), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
      {domains.map(({ domain, count }) => {
        const pct = Math.round((count / maxCount) * 100);
        const label = domain.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        return (
          <div key={domain} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
            <div style={{ width: '140px', flexShrink: 0, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={label}>
              {label}
            </div>
            <div style={{ flex: 1, background: 'var(--surface-2, rgba(99,102,241,0.08))', borderRadius: '3px', height: '14px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: 'var(--accent)', borderRadius: '3px', transition: 'width 0.3s ease' }} />
            </div>
            <div style={{ width: '32px', textAlign: 'right', color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>{count}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Cache Donut ─────────────────────────────────────────────────────────────

function CacheGauge({ pct }: { pct: number }) {
  // Simple arc SVG gauge
  const r = 28;
  const circ = 2 * Math.PI * r;
  const filled = (pct / 100) * circ;
  return (
    <svg viewBox="0 0 70 70" width="70" height="70" aria-label={`Cache hit rate: ${pct}%`}>
      <circle cx="35" cy="35" r={r} fill="none" stroke="var(--surface-2, rgba(99,102,241,0.08))" strokeWidth="10" />
      <circle
        cx="35" cy="35" r={r}
        fill="none"
        stroke="var(--accent)"
        strokeWidth="10"
        strokeDasharray={`${filled} ${circ - filled}`}
        strokeLinecap="round"
        transform="rotate(-90 35 35)"
      />
      <text x="35" y="35" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '14px', fontWeight: 700, fill: 'var(--text-primary)' }}>
        {pct}%
      </text>
    </svg>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface AnalyticsDashboardProps {
  isVisible: boolean;
}

export default function AnalyticsDashboard({ isVisible }: AnalyticsDashboardProps) {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chartView, setChartView] = useState<'queries' | 'users' | 'latency'>('queries');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/admin/analytics`);
      if (!res.ok) throw new Error(`Analytics fetch failed (${res.status})`);
      const json = await res.json() as AnalyticsData;
      setData(json);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isVisible) void load();
  }, [isVisible, load]);

  if (!isVisible) return null;

  const chartMeta: Record<typeof chartView, { valueKey: 'queries' | 'active_users' | 'avg_latency_ms'; label: string; color: string }> = {
    queries: { valueKey: 'queries', label: 'Queries per day', color: 'var(--accent)' },
    users: { valueKey: 'active_users', label: 'Active users per day', color: '#10b981' }, // kept for type-safety, toggle hidden
    latency: { valueKey: 'avg_latency_ms', label: 'Avg response time (ms)', color: '#f59e0b' },
  };

  const cm = chartMeta[chartView];

  return (
    <div className="cd-root">
      {/* Header */}
      <div className="cd-section-title">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 3v18h18"/>
          <path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/>
        </svg>
        Analytics Dashboard
        <button className="cd-refresh-btn" onClick={() => void load()} title="Refresh" aria-label="Refresh analytics" style={{ marginLeft: 'auto' }}>
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
        </button>
      </div>

      {loading && <div className="cd-loading">Loading analytics…</div>}
      {error && <div className="cd-error">{error}</div>}

      {data && (
        <>
          {/* Clause files hero */}
          <div className="cd-summary-row" style={{ marginBottom: '8px' }}>
            <div className="cd-summary-card" style={{ flex: '2 1 240px', background: 'var(--accent-soft, rgba(99,102,241,0.08))', borderColor: 'var(--accent)' }}>
              <div className="cd-summary-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                Clause files indexed
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
                <div className="cd-summary-value" style={{ fontSize: '2rem' }}>{CORPUS_STATS.clauses.toLocaleString()}</div>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>across {CORPUS_STATS.domains} standards</span>
              </div>
            </div>
          </div>

          {/* Summary cards */}
          <div className="cd-summary-row">
            <div className="cd-summary-card">
              <div className="cd-summary-label">Today's queries</div>
              <div className="cd-summary-value">{data.today.queries}</div>
            </div>
            <div className="cd-summary-card">
              <div className="cd-summary-label">Active users today</div>
              <div className="cd-summary-value">{data.today.active_users}</div>
            </div>
            <div className="cd-summary-card">
              <div className="cd-summary-label">Avg response time</div>
              <div className="cd-summary-value">{data.today.avg_latency_ms > 0 ? `${(data.today.avg_latency_ms / 1000).toFixed(1)}s` : '—'}</div>
            </div>
          </div>

          {/* Week summary */}
          <div className="cd-summary-row" style={{ marginTop: '0' }}>
            <div className="cd-summary-card">
              <div className="cd-summary-label">7-day queries</div>
              <div className="cd-summary-value">{data.week.queries}</div>
            </div>
            <div className="cd-summary-card">
              <div className="cd-summary-label">7-day active users</div>
              <div className="cd-summary-value">{data.week.active_users}</div>
            </div>
            <div className="cd-summary-card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div className="cd-summary-label">Cache hit rate</div>
              <CacheGauge pct={data.cache.hit_rate_pct} />
            </div>
          </div>

          {/* Chart toggles + chart */}
          <div className="cd-chart-section">
            <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
              {(['queries', 'latency'] as const).map(k => (
                <button
                  key={k}
                  onClick={() => setChartView(k as typeof chartView)}
                  style={{
                    padding: '3px 10px',
                    fontSize: '12px',
                    borderRadius: '12px',
                    border: `1px solid ${chartView === k ? 'var(--accent)' : 'var(--border)'}`,
                    background: chartView === k ? 'var(--accent)' : 'transparent',
                    color: chartView === k ? '#fff' : 'var(--text-secondary)',
                    cursor: 'pointer',
                  }}
                >
                  {k === 'queries' ? 'Queries' : 'Response Time'}
                </button>
              ))}
            </div>
            <div className="cd-chart-title">{cm.label} (30 days)</div>
            <BarChart data={data.daily} valueKey={cm.valueKey} label={cm.label} color={cm.color} height={80} />
          </div>

          {/* Top domains */}
          <div className="cd-table-section">
            <div className="cd-table-title">Most Queried Topics</div>
            <DomainChart domains={data.top_domains} />
          </div>

          {/* Cache stats */}
          <div className="cd-table-section" style={{ marginTop: '12px' }}>
            <div className="cd-table-title">Query Cache</div>
            <div className="cd-summary-row" style={{ marginTop: '8px' }}>
              <div className="cd-summary-card">
                <div className="cd-summary-label">Cached entries</div>
                <div className="cd-summary-value">{data.cache.entries}</div>
              </div>
              <div className="cd-summary-card">
                <div className="cd-summary-label">Total cache hits</div>
                <div className="cd-summary-value">{data.cache.total_hits}</div>
              </div>
              <div className="cd-summary-card">
                <div className="cd-summary-label">Hit rate</div>
                <div className="cd-summary-value">{data.cache.hit_rate_pct}%</div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
